import axios from "axios";
import { LibraryFileNode, LibraryFolderNode, LibraryLinkTarget, LibraryPortfolioView } from "../../API/Library/LibraryCommands";

export type LibraryCrumb = { id: string; title: string };

export const findFolder = (
  folders: LibraryFolderNode[],
  id: string,
  trail: LibraryCrumb[] = []
): { folder: LibraryFolderNode; trail: LibraryCrumb[] } | null => {
  for (const folder of folders) {
    const next = [...trail, { id: folder.id, title: folder.title }];
    if (folder.id === id) return { folder, trail: next };
    const nested = findFolder(folder.folders ?? [], id, next);
    if (nested) return nested;
  }
  return null;
};

export const folderContains = (folder: LibraryFolderNode, id: string): boolean =>
  folder.id === id || (folder.folders ?? []).some((child) => folderContains(child, id));

export const folderHoldsFile = (folder: LibraryFolderNode, fileId: string): boolean =>
  (folder.files ?? []).some((file) => file.id === fileId)
  || (folder.folders ?? []).some((child) => folderHoldsFile(child, fileId));

export type FolderDestination = { id: string; title: string; depth: number };

export const folderMoveDestinations = (
  folders: LibraryFolderNode[],
  movingId: string,
  rootTitle: string
): FolderDestination[] => {
  const located = findFolder(folders, movingId);
  const blocked = new Set<string>();
  const block = (folder: LibraryFolderNode) => {
    blocked.add(folder.id);
    (folder.folders ?? []).forEach(block);
  };
  if (located) block(located.folder);
  else blocked.add(movingId);

  const parentId = (ancestorIds(folders, movingId) ?? []).at(-1) ?? "";
  const rows: FolderDestination[] = [];
  if (parentId !== "") rows.push({ id: "", title: rootTitle || "Portfolio", depth: 0 });

  const walk = (list: LibraryFolderNode[], depth: number) => {
    for (const folder of list) {
      if (blocked.has(folder.id)) continue;
      if (folder.id !== parentId) rows.push({ id: folder.id, title: folder.title, depth });
      walk(folder.folders ?? [], depth + 1);
    }
  };
  walk(folders, parentId === "" ? 0 : 1);
  return rows;
};

export const findFileFolderId = (folders: LibraryFolderNode[], fileId: string): string | null => {
  for (const folder of folders) {
    if ((folder.files ?? []).some((file) => file.id === fileId)) return folder.id;
    const nested = findFileFolderId(folder.folders ?? [], fileId);
    if (nested) return nested;
  }
  return null;
};

export const findFileTitle = (folders: LibraryFolderNode[], fileId: string): string => {
  for (const folder of folders) {
    const file = (folder.files ?? []).find((item) => item.id === fileId);
    if (file) return file.title;
    const nested = findFileTitle(folder.folders ?? [], fileId);
    if (nested) return nested;
  }
  return "";
};

export const fileMoveDestinations = (
  folders: LibraryFolderNode[],
  currentFolderId: string
): FolderDestination[] => {
  const rows: FolderDestination[] = [];
  const walk = (list: LibraryFolderNode[], depth: number) => {
    for (const folder of list) {
      if (folder.id !== currentFolderId) rows.push({ id: folder.id, title: folder.title, depth });
      walk(folder.folders ?? [], depth + 1);
    }
  };
  walk(folders, 0);
  return rows;
};

export const moveDestinations = (
  folders: LibraryFolderNode[],
  fileIds: string[],
  folderIds: string[],
  currentFolderId: string,
  rootTitle: string
): FolderDestination[] => {
  if (fileIds.length === 0 && folderIds.length === 1) {
    return folderMoveDestinations(folders, folderIds[0], rootTitle);
  }
  if (folderIds.length === 0) return fileMoveDestinations(folders, currentFolderId);

  const blocked = new Set<string>();
  const block = (folder: LibraryFolderNode) => {
    blocked.add(folder.id);
    (folder.folders ?? []).forEach(block);
  };
  folderIds.forEach((id) => {
    const located = findFolder(folders, id);
    if (located) block(located.folder);
    else blocked.add(id);
  });

  const rows: FolderDestination[] = [];
  if (fileIds.length === 0 && currentFolderId !== "") {
    rows.push({ id: "", title: rootTitle || "Portfolio", depth: 0 });
  }
  const walk = (list: LibraryFolderNode[], depth: number) => {
    for (const folder of list) {
      if (blocked.has(folder.id)) continue;
      if (folder.id !== currentFolderId) rows.push({ id: folder.id, title: folder.title, depth });
      walk(folder.folders ?? [], depth + 1);
    }
  };
  walk(folders, rows.length > 0 ? 1 : 0);
  return rows;
};

export const ancestorIds = (folders: LibraryFolderNode[], id: string, trail: string[] = []): string[] | null => {
  for (const folder of folders) {
    if (folder.id === id) return trail;
    const nested = ancestorIds(folder.folders ?? [], id, [...trail, folder.id]);
    if (nested) return nested;
  }
  return null;
};

export const matches = (folder: LibraryFolderNode, needle: string): LibraryFileNode[] => {
  const own = (folder.files ?? []).filter((file) =>
    file.title.toLowerCase().includes(needle)
    || (file.uploadedByName ?? "").toLowerCase().includes(needle)
    || (file.tags ?? []).some((tag) => tag.toLowerCase().includes(needle))
  );
  return [...own, ...(folder.folders ?? []).flatMap((child) => matches(child, needle))];
};

export const itemCountLabel = (folder: LibraryFolderNode): string => {
  const items = (folder.files?.length ?? 0) + (folder.folders?.length ?? 0);
  return items === 1 ? "1 item" : `${items} items`;
};

export const formatRelativeDate = (iso?: string): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 60) return "just now";
  const minutes = Math.round(abs / 60);
  if (minutes < 60) return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return days === 1 ? "1 day ago" : `${days} days ago`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  return date.toLocaleDateString();
};

export const formatFullDate = (iso?: string): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

export const initials = (name?: string): string => {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const isLibraryId = (value?: string) =>
  !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());

export const libraryDisplayTitle = (...values: (string | undefined)[]) =>
  values.find((value) => value?.trim() && !isLibraryId(value))?.trim() ?? "";

export const applyQuestionTitles = (links: LibraryLinkTarget[], questions: LibraryLinkTarget[]): LibraryLinkTarget[] =>
  links.map((link) => {
    if (libraryDisplayTitle(link.questionTitle)) return link;
    const match = questions.find((question) =>
      question.questionId === link.questionId && (!question.areaId || !link.areaId || question.areaId === link.areaId)
    );
    if (!match) return link;
    return {
      ...link,
      questionTitle: match.questionTitle || link.questionTitle,
      sectionTitle: match.sectionTitle || link.sectionTitle,
    };
  });

export const isTrackPlan = (type?: string) => (type || "").trim().toLowerCase() === "improve";

export const libraryProductLabel = (type?: string) =>
  isTrackPlan(type) ? "Track" : (type || "").trim() || "Evaluate";

export const libraryKindNoun = (type?: string, plural = false) => {
  if (isTrackPlan(type)) return plural ? "Track Plans" : "Track Plan";
  return plural ? "diagnostics" : "diagnostic";
};

export const libraryTargetNoun = (type?: string, plural = false) => {
  if (isTrackPlan(type)) return plural ? "objectives" : "objective";
  return plural ? "criteria" : "criterion";
};

export const libraryLinkLabel = (link: {
  areaTitle?: string;
  areaId?: string;
  type?: string;
  questionTitle?: string;
  questionId?: string;
}): string => {
  const name = link.areaTitle || link.areaId || libraryKindNoun(link.type);
  const question = link.questionTitle || link.questionId || "";
  return question ? `${name} · ${question}` : name;
};

export type LinkedEvidenceGroup = {
  key: string;
  type: string;
  areaTitle: string;
  sections: { title: string; questions: LibraryLinkTarget[] }[];
};

export const groupLinkedEvidence = (links: LibraryLinkTarget[]): LinkedEvidenceGroup[] => {
  const groups: LinkedEvidenceGroup[] = [];
  for (const link of links) {
    const key = `${link.type}:${link.areaId}`;
    let group = groups.find((item) => item.key === key);
    if (!group) {
      group = {
        key,
        type: link.type,
        areaTitle: link.areaTitle || link.areaId || libraryKindNoun(link.type),
        sections: [],
      };
      groups.push(group);
    }
    if (!link.questionId || !libraryDisplayTitle(link.questionTitle, link.questionId)) continue;
    const sectionTitle = libraryDisplayTitle(link.sectionTitle);
    let section = group.sections.find((item) => item.title === sectionTitle);
    if (!section) {
      section = { title: sectionTitle, questions: [] };
      group.sections.push(section);
    }
    section.questions.push(link);
  }
  return groups;
};

export const libraryErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (data && typeof data === "object" && "message" in data && typeof data.message === "string" && data.message.trim()) {
      return data.message;
    }
  }
  return fallback;
};

/** Who appears in Invite Existing User for a private portfolio. */
export const libraryInviteScopeNote = (portfolio: Pick<LibraryPortfolioView, "access" | "linkType" | "linkName" | "partnerLabel">): string | undefined => {
  if ((portfolio.access || "Private") !== "Private") return undefined;
  if (portfolio.linkType == null && portfolio.partnerLabel == null) return undefined;
  const label = (portfolio.partnerLabel || "Partner").toLowerCase();
  const name = portfolio.linkName?.trim();
  if (portfolio.linkType === "Partner") {
    return name
      ? `Only people associated with ${name} are listed. This portfolio is linked to that ${label}, so people outside it cannot be invited.`
      : `Only people associated with the linked ${label} are listed. People outside it cannot be invited.`;
  }
  if (portfolio.linkType === "Department") {
    return name
      ? `Only people associated with ${name} are listed. This portfolio is linked to that department, so people outside it cannot be invited.`
      : `Only people associated with the linked department are listed. People outside it cannot be invited.`;
  }
  return `Anyone in this client can be invited. This portfolio is not linked to a ${label} or department.`;
};

export const patchFileInTree = (
  folders: LibraryFolderNode[],
  fileId: string,
  nextFile: LibraryFileNode
): LibraryFolderNode[] =>
  folders.map((folder) => ({
    ...folder,
    files: (folder.files ?? []).map((file) => (file.id === fileId ? { ...file, ...nextFile } : file)),
    folders: patchFileInTree(folder.folders ?? [], fileId, nextFile),
  }));
